import { useState } from "react";

interface DayActivity {
  time: string;
  activity: string;
  type: "sightseeing" | "food" | "transport" | "rest" | "adventure";
  duration: string;
  tip?: string;
}

interface DayPlan {
  day: number;
  title: string;
  activities: DayActivity[];
}

interface PlaceVisitPlan {
  [days: number]: DayPlan[];
}

const activityIcons: Record<string, string> = {
  sightseeing: "ri-camera-line",
  food: "ri-restaurant-line",
  transport: "ri-car-line",
  rest: "ri-hotel-line",
  adventure: "ri-run-line",
};

const activityColors: Record<string, string> = {
  sightseeing: "bg-amber-100 text-amber-600",
  food: "bg-rose-100 text-rose-600",
  transport: "bg-stone-100 text-stone-600",
  rest: "bg-sky-100 text-sky-600",
  adventure: "bg-emerald-100 text-emerald-600",
};

const visitPlans: Record<string, PlaceVisitPlan> = {
  "kapadokya": {
    2: [
      {
        day: 1,
        title: "Göreme & Peri Bacaları",
        activities: [
          { time: "05:30", activity: "Gün doğumu için erken kalkış", type: "adventure", duration: "30 dk" },
          { time: "06:00", activity: "Balon turu (opsiyonel, rezervasyon gerekli)", type: "adventure", duration: "1.5 saat", tip: "3-4 gün önceden rezervasyon yapın" },
          { time: "09:00", activity: "Göreme Açık Hava Müzesi", type: "sightseeing", duration: "2 saat" },
          { time: "12:00", activity: "Göreme'de öğle yemeği — Pumpkin Restaurant", type: "food", duration: "1 saat" },
          { time: "14:00", activity: "Güvercinlik Vadisi yürüyüşü", type: "adventure", duration: "2 saat" },
          { time: "17:00", activity: "Uçhisar Kalesi'nden gün batımı", type: "sightseeing", duration: "1.5 saat" },
          { time: "19:30", activity: "Ürgüp'te akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 2,
        title: "Yeraltı Şehirleri & Vadiler",
        activities: [
          { time: "09:00", activity: "Derinkuyu Yeraltı Şehri", type: "sightseeing", duration: "2 saat", tip: "Dar geçitler var, klaustrofobiniz varsa dikkat" },
          { time: "12:00", activity: "Mustafapaşa'da öğle yemeği", type: "food", duration: "1 saat" },
          { time: "14:00", activity: "Ihlara Vadisi yürüyüşü", type: "adventure", duration: "3 saat" },
          { time: "18:00", activity: "Avanos'ta çömlek atölyesi", type: "sightseeing", duration: "1 saat" },
          { time: "20:00", activity: "Göreme'de veda yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
    ],
    4: [
      {
        day: 1,
        title: "Varış & Göreme Keşfi",
        activities: [
          { time: "10:00", activity: "Varış ve otel check-in", type: "rest", duration: "1 saat" },
          { time: "11:30", activity: "Göreme köyü gezisi", type: "sightseeing", duration: "1.5 saat" },
          { time: "13:00", activity: "Yerel restoranda öğle yemeği", type: "food", duration: "1 saat" },
          { time: "15:00", activity: "Göreme Açık Hava Müzesi", type: "sightseeing", duration: "2 saat" },
          { time: "18:00", activity: "Uçhisar Kalesi gün batımı", type: "sightseeing", duration: "1.5 saat" },
          { time: "20:00", activity: "Ürgüp'te akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 2,
        title: "Balon & Vadiler",
        activities: [
          { time: "05:30", activity: "Balon turu hazırlık", type: "adventure", duration: "30 dk", tip: "Önceden rezervasyon şart!" },
          { time: "06:00", activity: "Sıcak hava balonu turu", type: "adventure", duration: "1.5 saat" },
          { time: "09:00", activity: "Kahvaltı & dinlenme", type: "food", duration: "1.5 saat" },
          { time: "11:00", activity: "Güvercinlik Vadisi yürüyüşü", type: "adventure", duration: "2 saat" },
          { time: "14:00", activity: "Kızılçukur Vadisi (Kırmızı Vadi)", type: "adventure", duration: "2 saat" },
          { time: "17:00", activity: "Çarşamba Pazarı (varsa)", type: "sightseeing", duration: "1 saat" },
          { time: "19:30", activity: "Göreme'de akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 3,
        title: "Yeraltı & Tarihi Köyler",
        activities: [
          { time: "09:00", activity: "Derinkuyu Yeraltı Şehri", type: "sightseeing", duration: "2 saat" },
          { time: "12:00", activity: "Mustafapaşa'da öğle yemeği", type: "food", duration: "1 saat" },
          { time: "14:00", activity: "Ihlara Vadisi yürüyüşü", type: "adventure", duration: "3 saat" },
          { time: "18:00", activity: "Selime Manastırı", type: "sightseeing", duration: "1 saat" },
          { time: "20:00", activity: "Aksaray'da akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 4,
        title: "Avanos & Veda",
        activities: [
          { time: "09:00", activity: "Avanos çömlek atölyesi", type: "sightseeing", duration: "1.5 saat" },
          { time: "11:00", activity: "Paşabağ Mantar Kayaları", type: "sightseeing", duration: "1 saat" },
          { time: "13:00", activity: "Son öğle yemeği & alışveriş", type: "food", duration: "1.5 saat" },
          { time: "15:00", activity: "Havalimanına transfer", type: "transport", duration: "1 saat" },
        ],
      },
    ],
    7: [
      {
        day: 1,
        title: "Varış & Göreme",
        activities: [
          { time: "10:00", activity: "Varış ve otel check-in", type: "rest", duration: "1 saat" },
          { time: "12:00", activity: "Göreme köyü keşfi", type: "sightseeing", duration: "2 saat" },
          { time: "15:00", activity: "Göreme Açık Hava Müzesi", type: "sightseeing", duration: "2 saat" },
          { time: "18:00", activity: "Uçhisar gün batımı", type: "sightseeing", duration: "1.5 saat" },
          { time: "20:00", activity: "Ürgüp'te akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 2,
        title: "Balon Turu & Kırmızı Vadi",
        activities: [
          { time: "05:30", activity: "Balon turu", type: "adventure", duration: "2 saat" },
          { time: "09:00", activity: "Kahvaltı", type: "food", duration: "1 saat" },
          { time: "11:00", activity: "Kızılçukur Vadisi yürüyüşü", type: "adventure", duration: "3 saat" },
          { time: "15:00", activity: "Çavuşin köyü", type: "sightseeing", duration: "1 saat" },
          { time: "19:00", activity: "Akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 3,
        title: "Güvercinlik & Aşk Vadisi",
        activities: [
          { time: "09:00", activity: "Güvercinlik Vadisi", type: "adventure", duration: "2 saat" },
          { time: "12:00", activity: "Öğle yemeği", type: "food", duration: "1 saat" },
          { time: "14:00", activity: "Aşk Vadisi yürüyüşü", type: "adventure", duration: "2 saat" },
          { time: "17:00", activity: "Avanos çömlek atölyesi", type: "sightseeing", duration: "1.5 saat" },
          { time: "20:00", activity: "Akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 4,
        title: "Yeraltı Şehirleri",
        activities: [
          { time: "09:00", activity: "Derinkuyu Yeraltı Şehri", type: "sightseeing", duration: "2 saat" },
          { time: "12:00", activity: "Öğle yemeği", type: "food", duration: "1 saat" },
          { time: "14:00", activity: "Kaymaklı Yeraltı Şehri", type: "sightseeing", duration: "1.5 saat" },
          { time: "17:00", activity: "Mustafapaşa köyü", type: "sightseeing", duration: "1.5 saat" },
          { time: "20:00", activity: "Akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 5,
        title: "Ihlara Vadisi",
        activities: [
          { time: "09:00", activity: "Ihlara Vadisi tam yürüyüşü", type: "adventure", duration: "4 saat" },
          { time: "14:00", activity: "Selime Manastırı", type: "sightseeing", duration: "1.5 saat" },
          { time: "17:00", activity: "Aksaray gezisi", type: "sightseeing", duration: "1.5 saat" },
          { time: "20:00", activity: "Akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 6,
        title: "Paşabağ & Zelve",
        activities: [
          { time: "09:00", activity: "Paşabağ Mantar Kayaları", type: "sightseeing", duration: "1.5 saat" },
          { time: "11:00", activity: "Zelve Açık Hava Müzesi", type: "sightseeing", duration: "2 saat" },
          { time: "14:00", activity: "Öğle yemeği", type: "food", duration: "1 saat" },
          { time: "16:00", activity: "Ürgüp şarap tadımı", type: "food", duration: "2 saat" },
          { time: "19:30", activity: "Veda yemeği", type: "food", duration: "2 saat" },
        ],
      },
      {
        day: 7,
        title: "Son Gün & Ayrılış",
        activities: [
          { time: "09:00", activity: "Sabah kahvaltısı & alışveriş", type: "food", duration: "2 saat" },
          { time: "12:00", activity: "Havalimanına transfer", type: "transport", duration: "1 saat" },
        ],
      },
    ],
  },
  "mardin": {
    2: [
      {
        day: 1,
        title: "Tarihi Çarşı & Manastır",
        activities: [
          { time: "09:00", activity: "Deyrulzafaran Manastırı", type: "sightseeing", duration: "2 saat", tip: "Sabah erken gidin, kalabalık olmaz" },
          { time: "12:00", activity: "Tarihi çarşıda öğle yemeği", type: "food", duration: "1 saat" },
          { time: "14:00", activity: "Kasımiye Medresesi", type: "sightseeing", duration: "1 saat" },
          { time: "16:00", activity: "Mardin Müzesi", type: "sightseeing", duration: "1.5 saat" },
          { time: "18:00", activity: "Terasta gün batımı seyri", type: "sightseeing", duration: "1 saat" },
          { time: "20:00", activity: "Akşam yemeği — Cercis Murat Konağı", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 2,
        title: "Midyat & Mor Gabriel",
        activities: [
          { time: "09:00", activity: "Midyat'a gidiş (30 dk)", type: "transport", duration: "30 dk" },
          { time: "10:00", activity: "Mor Gabriel Manastırı", type: "sightseeing", duration: "2 saat" },
          { time: "13:00", activity: "Midyat'ta öğle yemeği", type: "food", duration: "1 saat" },
          { time: "15:00", activity: "Midyat çarşısı & gümüş takı", type: "sightseeing", duration: "1.5 saat" },
          { time: "17:00", activity: "Mardin'e dönüş", type: "transport", duration: "30 dk" },
          { time: "19:00", activity: "Veda yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
    ],
    3: [
      {
        day: 1,
        title: "Mardin Tarihi Merkez",
        activities: [
          { time: "10:00", activity: "Varış ve otel check-in", type: "rest", duration: "1 saat" },
          { time: "11:30", activity: "Mardin Kalesi manzarası", type: "sightseeing", duration: "1 saat" },
          { time: "13:00", activity: "Öğle yemeği", type: "food", duration: "1 saat" },
          { time: "15:00", activity: "Kasımiye Medresesi & Ulu Cami", type: "sightseeing", duration: "2 saat" },
          { time: "18:00", activity: "Gün batımı terası", type: "sightseeing", duration: "1 saat" },
          { time: "20:00", activity: "Akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 2,
        title: "Manastırlar Günü",
        activities: [
          { time: "09:00", activity: "Deyrulzafaran Manastırı", type: "sightseeing", duration: "2 saat" },
          { time: "12:00", activity: "Öğle yemeği", type: "food", duration: "1 saat" },
          { time: "14:00", activity: "Mor Gabriel Manastırı (Midyat)", type: "sightseeing", duration: "2 saat" },
          { time: "17:00", activity: "Midyat çarşısı", type: "sightseeing", duration: "1.5 saat" },
          { time: "20:00", activity: "Akşam yemeği", type: "food", duration: "1.5 saat" },
        ],
      },
      {
        day: 3,
        title: "Dara & Veda",
        activities: [
          { time: "09:00", activity: "Dara Antik Kenti", type: "sightseeing", duration: "2 saat" },
          { time: "12:00", activity: "Son öğle yemeği & alışveriş", type: "food", duration: "1.5 saat" },
          { time: "14:00", activity: "Havalimanına transfer", type: "transport", duration: "30 dk" },
        ],
      },
    ],
  },
  "bodrum": {
    3: [
      {
        day: 1,
        title: "Bodrum Merkez & Kale",
        activities: [
          { time: "10:00", activity: "Bodrum Kalesi & Sualtı Müzesi", type: "sightseeing", duration: "2 saat" },
          { time: "13:00", activity: "Balık restoranında öğle yemeği", type: "food", duration: "1.5 saat" },
          { time: "15:00", activity: "Halikarnas Mozolesi kalıntıları", type: "sightseeing", duration: "1 saat" },
          { time: "17:00", activity: "Bodrum çarşısı & alışveriş", type: "sightseeing", duration: "2 saat" },
          { time: "20:00", activity: "Akşam yemeği — Bodrum limanı", type: "food", duration: "2 saat" },
        ],
      },
      {
        day: 2,
        title: "Gümüşlük & Türkbükü",
        activities: [
          { time: "09:00", activity: "Gümüşlük'e gidiş", type: "transport", duration: "30 dk" },
          { time: "10:00", activity: "Gümüşlük plajı & yüzme", type: "adventure", duration: "2 saat" },
          { time: "13:00", activity: "Gümüşlük'te balık yemeği", type: "food", duration: "1.5 saat" },
          { time: "15:30", activity: "Türkbükü'ne gidiş", type: "transport", duration: "30 dk" },
          { time: "16:00", activity: "Türkbükü plajı", type: "adventure", duration: "2 saat" },
          { time: "19:30", activity: "Türkbükü'nde akşam yemeği", type: "food", duration: "2 saat" },
        ],
      },
      {
        day: 3,
        title: "Tekne Turu & Veda",
        activities: [
          { time: "09:00", activity: "Günlük tekne turu", type: "adventure", duration: "6 saat", tip: "Koylar arası yüzme dahil" },
          { time: "16:00", activity: "Dönüş & dinlenme", type: "rest", duration: "1 saat" },
          { time: "19:00", activity: "Veda yemeği", type: "food", duration: "2 saat" },
        ],
      },
    ],
    5: [
      {
        day: 1,
        title: "Bodrum Merkez",
        activities: [
          { time: "10:00", activity: "Varış ve otel check-in", type: "rest", duration: "1 saat" },
          { time: "12:00", activity: "Bodrum Kalesi", type: "sightseeing", duration: "2 saat" },
          { time: "15:00", activity: "Bodrum çarşısı", type: "sightseeing", duration: "2 saat" },
          { time: "20:00", activity: "Akşam yemeği", type: "food", duration: "2 saat" },
        ],
      },
      {
        day: 2,
        title: "Gümüşlük & Yalıkavak",
        activities: [
          { time: "09:00", activity: "Gümüşlük plajı", type: "adventure", duration: "3 saat" },
          { time: "13:00", activity: "Gümüşlük'te öğle yemeği", type: "food", duration: "1.5 saat" },
          { time: "15:30", activity: "Yalıkavak Marina", type: "sightseeing", duration: "2 saat" },
          { time: "19:00", activity: "Akşam yemeği", type: "food", duration: "2 saat" },
        ],
      },
      {
        day: 3,
        title: "Tekne Turu",
        activities: [
          { time: "09:00", activity: "Günlük tekne turu", type: "adventure", duration: "7 saat" },
          { time: "17:00", activity: "Dönüş & dinlenme", type: "rest", duration: "2 saat" },
          { time: "20:00", activity: "Akşam yemeği", type: "food", duration: "2 saat" },
        ],
      },
      {
        day: 4,
        title: "Türkbükü & Bitez",
        activities: [
          { time: "10:00", activity: "Türkbükü plajı", type: "adventure", duration: "3 saat" },
          { time: "14:00", activity: "Bitez plajı", type: "adventure", duration: "3 saat" },
          { time: "19:00", activity: "Akşam yemeği", type: "food", duration: "2 saat" },
        ],
      },
      {
        day: 5,
        title: "Son Gün & Ayrılış",
        activities: [
          { time: "09:00", activity: "Sabah kahvaltısı & alışveriş", type: "food", duration: "2 saat" },
          { time: "12:00", activity: "Havalimanına transfer", type: "transport", duration: "1 saat" },
        ],
      },
    ],
  },
};

// Generic plan generator for places without specific plans
const generateGenericPlan = (placeName: string, days: number): DayPlan[] => {
  const plans: DayPlan[] = [];
  for (let d = 1; d <= days; d++) {
    const activities: DayActivity[] = [];
    if (d === 1) {
      activities.push(
        { time: "10:00", activity: `${placeName}'e varış ve otel check-in`, type: "rest", duration: "1 saat" },
        { time: "12:00", activity: "Öğle yemeği — yerel restoran", type: "food", duration: "1 saat" },
        { time: "14:00", activity: "Ana tarihi merkez gezisi", type: "sightseeing", duration: "3 saat" },
        { time: "18:00", activity: "Gün batımı seyri", type: "sightseeing", duration: "1 saat" },
        { time: "20:00", activity: "Akşam yemeği", type: "food", duration: "1.5 saat" },
      );
    } else if (d === days) {
      activities.push(
        { time: "09:00", activity: "Sabah kahvaltısı & son gezinti", type: "food", duration: "1.5 saat" },
        { time: "11:00", activity: "Alışveriş & hediyelik", type: "sightseeing", duration: "1.5 saat" },
        { time: "13:00", activity: "Veda yemeği", type: "food", duration: "1 saat" },
        { time: "15:00", activity: "Ayrılış", type: "transport", duration: "1 saat" },
      );
    } else {
      activities.push(
        { time: "09:00", activity: `${d}. gün — Doğa & Kültür Turu`, type: "sightseeing", duration: "3 saat" },
        { time: "13:00", activity: "Öğle yemeği", type: "food", duration: "1 saat" },
        { time: "15:00", activity: "Müze veya tarihi alan ziyareti", type: "sightseeing", duration: "2 saat" },
        { time: "18:00", activity: "Serbest zaman", type: "rest", duration: "1.5 saat" },
        { time: "20:00", activity: "Akşam yemeği", type: "food", duration: "1.5 saat" },
      );
    }
    plans.push({ day: d, title: d === 1 ? "Varış & İlk Keşif" : d === days ? "Son Gün & Ayrılış" : `${d}. Gün Keşfi`, activities });
  }
  return plans;
};

interface VisitPlanModalProps {
  placeId: string;
  placeName: string;
  onClose: () => void;
}

const VisitPlanModal = ({ placeId, placeName, onClose }: VisitPlanModalProps) => {
  const availableDays = visitPlans[placeId] ? Object.keys(visitPlans[placeId]).map(Number).sort((a, b) => a - b) : [2, 3, 5];
  const [selectedDays, setSelectedDays] = useState(availableDays[0] || 2);
  const [activeDay, setActiveDay] = useState(1);

  const planData = visitPlans[placeId]?.[selectedDays] || generateGenericPlan(placeName, selectedDays);

  const handleDaysChange = (days: number) => {
    setSelectedDays(days);
    setActiveDay(1);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <div>
            <h2 className="font-bold text-stone-900 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ziyaret Planı
            </h2>
            <p className="text-stone-400 text-xs mt-0.5">{placeName} için günlük program önerisi</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-stone-100 hover:bg-stone-200 rounded-full cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-stone-600"></i>
          </button>
        </div>

        {/* Day Selector */}
        <div className="px-6 py-4 border-b border-stone-50">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Kaç gün kalacaksınız?</p>
          <div className="flex gap-2 flex-wrap">
            {availableDays.map((d) => (
              <button
                key={d}
                onClick={() => handleDaysChange(d)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedDays === d
                    ? "bg-stone-900 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                <i className="ri-calendar-line text-xs"></i>
                {d} Gün
              </button>
            ))}
          </div>
        </div>

        {/* Day Tabs */}
        <div className="px-6 pt-4 flex gap-2 overflow-x-auto pb-2 flex-shrink-0">
          {planData.map((day) => (
            <button
              key={day.day}
              onClick={() => setActiveDay(day.day)}
              className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeDay === day.day
                  ? "bg-amber-500 text-white"
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
            >
              {day.day}. Gün
            </button>
          ))}
        </div>

        {/* Day Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {planData
            .filter((d) => d.day === activeDay)
            .map((day) => (
              <div key={day.day}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 flex items-center justify-center bg-amber-500 rounded-full">
                    <span className="text-white text-xs font-bold">{day.day}</span>
                  </div>
                  <h3 className="font-bold text-stone-900 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {day.title}
                  </h3>
                </div>

                <div className="space-y-3">
                  {day.activities.map((activity, idx) => (
                    <div key={idx} className="flex gap-3">
                      {/* Time */}
                      <div className="flex-shrink-0 w-14 text-right">
                        <span className="text-xs font-semibold text-stone-400">{activity.time}</span>
                      </div>

                      {/* Line */}
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full ${activityColors[activity.type]}`}>
                          <i className={`${activityIcons[activity.type]} text-xs`}></i>
                        </div>
                        {idx < day.activities.length - 1 && (
                          <div className="w-px flex-1 bg-stone-100 mt-1 min-h-4"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-3">
                        <p className="text-sm font-medium text-stone-800 leading-snug">{activity.activity}</p>
                        <p className="text-xs text-stone-400 mt-0.5">{activity.duration}</p>
                        {activity.tip && (
                          <div className="flex items-start gap-1.5 mt-1.5 bg-amber-50 rounded-lg px-2.5 py-1.5">
                            <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-lightbulb-line text-amber-500 text-xs"></i>
                            </div>
                            <p className="text-xs text-amber-700">{activity.tip}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-between gap-3">
          <p className="text-xs text-stone-400">
            <i className="ri-information-line mr-1"></i>
            Süreler tahminidir, kişisel temponuza göre ayarlayın.
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-stone-900 text-white text-sm font-semibold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Planı Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisitPlanModal;
